/**
 * useUnsavedChangesGuard - Hook for protecting against accidental navigation with unsaved changes.
 *
 * Provides:
 * - Browser close/refresh protection via beforeunload
 * - React Router navigation blocking via useBlocker
 * - Guard function for custom navigation triggers (close buttons, etc.)
 * - Modal props for integration with UnsavedChangesModal (3-button: Save / Discard / Cancel)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';

import type { UnsavedChangesModalProps } from '@/lib/base/overlays/unsaved-changes-modal';
import { logger } from '@/lib/logger';

// Re-exported so existing consumers that read the modal-props type from the
// hook keep working.
export type { UnsavedChangesModalProps };

/**
 * Options for useUnsavedChangesGuard hook.
 */
export interface UseUnsavedChangesGuardOptions {
  /** Whether there are unsaved changes to protect */
  hasUnsavedChanges: boolean;
  /** Callback to discard changes when user confirms leaving */
  onDiscard: () => void;
  /** Callback to save changes. When provided, the modal shows a "Save Changes" button. */
  onSave?: () => Promise<void>;
  /** Whether to block React Router navigation (default: true) */
  blockRouterNavigation?: boolean;
  /** Whether to block browser close/refresh (default: true) */
  blockBrowserClose?: boolean;
}

/**
 * Result returned by useUnsavedChangesGuard hook.
 */
export interface UseUnsavedChangesGuardResult {
  /** Guard function that blocks action if unsaved changes exist. Returns true if action should proceed. */
  guard: (action: () => void) => boolean;
  /** Props to spread to UnsavedChangesModal */
  modalProps: UnsavedChangesModalProps;
  /** Whether the guard is currently blocking navigation/action */
  isBlocking: boolean;
}

/**
 * Hook for protecting against accidental navigation with unsaved changes.
 *
 * @example
 * ```tsx
 * const { guard, modalProps, isBlocking } = useUnsavedChangesGuard({
 *   hasUnsavedChanges,
 *   onDiscard: resetChanges,
 *   onSave: saveChanges,
 * });
 *
 * const handleClose = () => {
 *   if (guard(onClose)) {
 *     onClose();
 *   }
 * };
 *
 * return (
 *   <>
 *     <YourComponent />
 *     <UnsavedChangesModal {...modalProps} />
 *   </>
 * );
 * ```
 */
export function useUnsavedChangesGuard({
  hasUnsavedChanges,
  onDiscard,
  onSave,
  blockRouterNavigation = true,
  blockBrowserClose = true,
}: UseUnsavedChangesGuardOptions): UseUnsavedChangesGuardResult {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Block browser close/refresh with native dialog
  useEffect(() => {
    if (!blockBrowserClose || !hasUnsavedChanges) {
      return;
    }

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [blockBrowserClose, hasUnsavedChanges]);

  // Block React Router navigation
  const blocker = useBlocker(blockRouterNavigation && hasUnsavedChanges);

  // Handle blocker state changes
  useEffect(() => {
    if (blocker?.state === 'blocked') {
      pendingActionRef.current = () => blocker.proceed();
      setIsModalOpen(true);
    }
  }, [blocker]);

  // Auto-close modal and execute pending action if blocking condition clears while modal is open.
  // This handles the case where a save triggered from the modal completes successfully -
  // hasUnsavedChanges becomes false, so we should proceed with the originally-requested action
  // (e.g., closing the slideout) rather than just closing the modal.
  useEffect(() => {
    if (isModalOpen && !hasUnsavedChanges) {
      // Execute the pending action (e.g., close slideout) now that changes are saved
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      setIsModalOpen(false);

      if (action) {
        action();
      } else if (blocker?.state === 'blocked') {
        blocker.proceed();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- blocker excluded: useBlocker returns new object each render, including it would cause infinite re-renders
  }, [isModalOpen, hasUnsavedChanges]);

  // Guard function for custom navigation triggers
  const guard = useCallback(
    (action: () => void): boolean => {
      if (!hasUnsavedChanges) {
        return true;
      }

      pendingActionRef.current = action;
      setIsModalOpen(true);
      return false;
    },
    [hasUnsavedChanges]
  );

  // Execute the pending action and clean up
  const executePendingAction = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setIsModalOpen(false);

    if (action) {
      action();
    } else if (blocker?.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  // Handle save: save changes then execute pending action
  const handleSave = useCallback(async () => {
    if (!onSave) {
      return;
    }
    setSaveError(null);
    setIsSaving(true);
    try {
      await onSave();
      setIsSaving(false);
      executePendingAction();
    } catch (err) {
      // Keep modal open on save failure — user can retry or discard
      logger.error(
        'useUnsavedChangesGuard: save failed',
        err instanceof Error ? err : new Error(String(err))
      );
      setSaveError("We couldn't save your changes. Try again.");
      setIsSaving(false);
    }
  }, [onSave, executePendingAction]);

  // Handle discard: discard changes and execute pending action
  const handleDiscard = useCallback(() => {
    setSaveError(null);
    onDiscard();
    executePendingAction();
  }, [onDiscard, executePendingAction]);

  // Handle cancel: close modal and reset blocker
  const handleCancel = useCallback(() => {
    setSaveError(null);
    pendingActionRef.current = null;
    setIsModalOpen(false);

    if (blocker?.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const modalProps: UnsavedChangesModalProps = {
    isOpen: isModalOpen,
    onSave: onSave ? handleSave : null,
    onDiscard: handleDiscard,
    onCancel: handleCancel,
    isSaving,
    saveError,
  };

  return {
    guard,
    modalProps,
    isBlocking: isModalOpen,
  };
}
